// ────────────────────────────────────────────────────────────────────────
// Vista Forecasting & Programación.
//
// Consume microservice-forecasting (vía BFF) y pinta lo que el backend YA
// produce: para cada métrica (pedidos creados, entregas, entregas en SLA) y
// granularidad (diaria 90 días / semanal 26 semanas / mensual 12 meses) una
// curva de proyección con intervalos de predicción 80/95 ("rango probable"), su
// error en el backtest de la corrida vigente (real vs predicho) y el calendario
// de eventos. El período en curso (semana o mes) combina lo ya realizado
// (actualToDate) con la proyección del resto.
//
// Lo que el backend todavía NO entrega (capacidad de flota, curva intradía,
// recomendaciones) NO se muestra: esta vista refleja el dato real, sin inventar.
// ────────────────────────────────────────────────────────────────────────
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from "recharts";
import {
  Package, Truck, ShieldCheck, Brain, Activity, TrendingUp, CalendarPlus,
  RefreshCw, AlertTriangle, Info, Target, BarChart3,
} from "lucide-react";
import useTheme from "hooks/useTheme";
import { cn } from "../../../utils/cn";
import { formatNumberCL, formatPercent } from "../../../utils/formatters";
import { useUrlState } from "../../../hooks/useUrlState";
import { RelativeTime } from "../../../components/ui/RelativeTime";
import useForecastingData from "./useForecastingData";
import { selectHistoryForSerie, wapeHint, accuracySubtitle } from "./accuracySummary";
import EventsModal from "./EventsModal";
import "./forecasting.scss";

const PALETTE = {
  light: { brand: "#F97316", band: "#F59E0B", real: "#229E5C", tick: "#666666", grid: "#E3E3E3", ref: "#171717" },
  dark: { brand: "#FB923C", band: "#FBBF24", real: "#22C55E", tick: "#9E9E9E", grid: "#292929", ref: "#FAFAFA" },
};

const METRICS = [
  { key: "created", label: "Pedidos creados", short: "Creados", icon: Package, unit: "pedidos" },
  { key: "delivered", label: "Entregas", short: "Entregas", icon: Truck, unit: "entregas" },
  { key: "delivered_before_sla", label: "Entregas en SLA", short: "En SLA", icon: ShieldCheck, unit: "entregas" },
];
const GRANS = [
  { key: "daily", label: "Diaria" },
  { key: "weekly", label: "Semanal" },
  { key: "monthly", label: "Mensual" },
];
const HORIZONS = [30, 60, 90];
const FAMILY_LABEL = { REGULAR: "Demanda regular", INTERMITTENT: "Demanda intermitente" };
const KIND_LABEL = { holiday: "Feriado", peak: "Pico de demanda", promo: "Campaña / Promo", other: "Otro" };
// Cuántos períodos pasados (real + lo que proyectamos) mostrar antes de la proyección.
const PAST_PERIODS = { daily: 7, weekly: 8, monthly: 13 };
// Horizonte publicado por granularidad (lo define el backend).
const HORIZON_TEXT = {
  daily: "Proyección diaria a 90 días",
  weekly: "Proyección semanal a 26 semanas",
  monthly: "Proyección mensual a 12 meses",
};

const fmt = (n) => formatNumberCL(Math.round(Number(n || 0)));

const toDate = (iso) => new Date(`${iso}T00:00:00`);
const addDays = (iso, n) => {
  const d = toDate(iso);
  d.setDate(d.getDate() + n);
  return d;
};

// Rango de una semana. El backend nuevo manda periodStart/periodEnd (lunes-domingo);
// el anterior etiquetaba por el LUNES DE TÉRMINO de una semana martes-lunes. Con el
// fallback la vista funciona con cualquiera de las dos versiones del backend.
function weekRange(pt) {
  if (pt?.periodStart && pt?.periodEnd) return [toDate(pt.periodStart), toDate(pt.periodEnd)];
  return [addDays(pt.targetDate, -6), toDate(pt.targetDate)];
}

function fechaLabel(pt, gran) {
  const iso = pt.targetDate;
  if (gran === "monthly") {
    return toDate(pt.periodStart || iso).toLocaleDateString("es-CL", { month: "short", year: "numeric" });
  }
  if (gran === "weekly") {
    const [a, b] = weekRange(pt);
    const fmtDay = (d) => d.toLocaleDateString("es-CL", { day: "numeric", month: "short" });
    return `${fmtDay(a)} – ${fmtDay(b)}`;
  }
  return toDate(iso).toLocaleDateString("es-CL", { weekday: "short", day: "numeric", month: "short" });
}

// ¿La fecha (YYYY-MM-DD) cae dentro del evento? Espeja la lógica del backend
// (events_overlay.date_in_event) para marcar los eventos sobre la curva.
function eventCoversDate(ev, iso) {
  const start = ev.startDate;
  if (!start) return false;
  const end = ev.endDate || start;
  if ((ev.recurrence || "none") === "annual") {
    const md = (s) => { const p = s.split("-"); return Number(p[1]) * 100 + Number(p[2]); };
    const d = new Date(`${iso}T00:00:00`);
    const cur = (d.getMonth() + 1) * 100 + d.getDate();
    const s = md(start);
    const e = md(end);
    return s <= e ? cur >= s && cur <= e : cur >= s || cur <= e;
  }
  return start <= iso && iso <= end;
}

function KpiTile({ icon: Icon, tone = "brand", label, value, hint }) {
  const toneCls = {
    brand: "bg-brand-50 text-brand-700",
    success: "bg-status-success-soft text-status-success",
    warning: "bg-status-warning-soft text-status-warning",
    info: "bg-status-info-soft text-status-info",
    // Para métricas que no son buenas ni malas por sí solas (p. ej. el error
    // típico): un tono de éxito fijo las presentaba como un logro.
    neutral: "bg-bg-muted text-fg-muted",
  }[tone];
  return (
    <div className="bg-bg border border-border rounded-xl shadow-card p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-fg-muted">{label}</span>
        <span className={cn("w-8 h-8 rounded-lg flex items-center justify-center", toneCls)}>
          <Icon className="w-4 h-4" />
        </span>
      </div>
      <div className="text-2xl font-semibold text-fg tabular-nums leading-tight">{value}</div>
      {hint && <div className="text-[11px] text-fg-muted mt-1">{hint}</div>}
    </div>
  );
}

function ForecastTooltip({ active, payload }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="rounded-lg border border-border bg-bg shadow-elevated px-3 py-2 text-xs">
      <div className="font-semibold text-fg mb-1 flex items-center gap-2">
        {d.label}
        {d.past && <span className="px-1.5 py-0.5 rounded bg-bg-muted text-fg-muted text-[10px] font-bold uppercase">{t('real')}</span>}
        {d.inProgress && <span className="px-1.5 py-0.5 rounded bg-brand-50 text-brand-700 text-[10px] font-bold uppercase">{t('en curso')}</span>}
      </div>
      {d.inProgress && (
        <div className="text-[11px] text-fg-muted mb-1">
          {t('Real a la fecha')}: <strong className="text-fg tabular-nums">{fmt(d.actualToDate)}</strong>
          {' · '}{t('Proyectado resto')}: <strong className="text-fg tabular-nums">{fmt(Number(d.yhat) - Number(d.actualToDate))}</strong>
        </div>
      )}
      {d.real != null && (
        <div className="flex items-center gap-2 text-fg"><span className="w-2 h-2 rounded-full bg-status-success" />{t('Real')}: <strong>{fmt(d.real)}</strong></div>
      )}
      {d.yhat != null && (
        <div className="flex items-center gap-2 text-fg">
          <span className="w-2 h-2 rounded-full bg-brand-500" />
          {d.past ? t('Proyectado') : t('Proyección')}: <strong>{fmt(d.yhat)}</strong>
        </div>
      )}
      {d.banda80 && (
        <div className="text-[11px] text-fg-muted ml-4">{t('Rango probable 80%')}: {fmt(d.banda80[0])} – {fmt(d.banda80[1])}</div>
      )}
      {d.banda95 && (
        <div className="text-[11px] text-fg-muted ml-4">{t('Rango probable 95%')}: {fmt(d.banda95[0])} – {fmt(d.banda95[1])}</div>
      )}
    </div>
  );
}

function AccuracyTooltip({ active, payload }) {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="rounded-lg border border-border bg-bg shadow-elevated px-3 py-2 text-xs">
      <div className="font-semibold text-fg mb-1">{d.label}</div>
      <div className="flex items-center gap-2 text-fg">
        <span className="w-2 h-2 rounded-full bg-status-success" />{t('Real')}: <strong>{fmt(d.actual)}</strong>
      </div>
      <div className="flex items-center gap-2 text-fg">
        <span className="w-2 h-2 rounded-full bg-status-warning" />{t('Predicho')}: <strong>{fmt(d.yhat)}</strong>
      </div>
    </div>
  );
}

export default function ForecastingView() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const p = isDark ? PALETTE.dark : PALETTE.light;

  // Controles persistidos en la URL (deep-link/bookmark de la vista). Los defaults
  // son los mismos de antes ⇒ sin params el comportamiento es IDÉNTICO (params
  // omitidos al igualar el default). Solo presentación: no cambian la firma de fetch.
  const [metric, setMetric] = useUrlState("metric", "created");
  const [granularity, setGranularity] = useUrlState("granularity", "daily");
  const [horizon, setHorizon] = useUrlState("horizon", 30, { type: "number" });
  const [entityIdx, setEntityIdx] = useUrlState("entity", 0, { type: "number" });
  const [modalOpen, setModalOpen] = useState(false);
  const [show95, setShow95] = useUrlState("ci95", false, { type: "boolean" });

  const { loading, error, series, history, reload, events, reloadEvents } = useForecastingData({ metric, granularity });

  const metricMeta = METRICS.find((m) => m.key === metric) || METRICS[0];

  // En el caso común series.length === 1. En holding puede haber varias empresas.
  const safeIdx = entityIdx < series.length ? entityIdx : 0;
  const serie = series[safeIdx] || null;
  const hist = useMemo(() => selectHistoryForSerie(history, serie), [history, serie]);

  // Horizonte solo aplica a granularidad diaria; semanal/mensual muestran todo.
  const cutoffIso = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + (granularity === "daily" ? horizon : 9999));
    return d.toISOString().slice(0, 10);
  }, [horizon, granularity]);

  const futureData = useMemo(() => {
    const pts = (serie?.points || []).filter((pt) => pt.targetDate <= cutoffIso);
    return pts.map((pt) => ({
      targetDate: pt.targetDate,
      label: fechaLabel(pt, granularity),
      yhat: pt.yhat,
      inProgress: pt.actualToDate != null,
      actualToDate: pt.actualToDate,
      banda80: pt.lo80 != null && pt.hi80 != null ? [pt.lo80, pt.hi80] : undefined,
      banda95: pt.lo95 != null && pt.hi95 != null ? [pt.lo95, pt.hi95] : undefined,
    }));
  }, [serie, cutoffIso, granularity]);

  // Inicio de la proyección = primer targetDate futuro (lo define el backend, "hoy"
  // en la zona de operación). Todo lo anterior es pasado realizado.
  const firstFutureDate = futureData[0]?.targetDate;
  const todayLabel = futureData[0]?.label;

  // Pasado reciente: últimos N períodos con su REAL + lo que el modelo vigente
  // proyecta para esas fechas sin haberlas visto (backtest de la corrida vigente
  // en forecast_accuracy, por granularidad).
  const pastData = useMemo(() => {
    if (!firstFutureDate) return [];
    const n = PAST_PERIODS[granularity] || 7;
    const pts = (hist?.points || []).filter((q) => q.actual != null && q.targetDate < firstFutureDate);
    return pts.slice(-n).map((q) => ({
      targetDate: q.targetDate,
      label: fechaLabel(q, granularity),
      real: q.actual,
      yhat: q.yhat,
      past: true,
    }));
  }, [hist, granularity, firstFutureDate]);

  const mergedData = useMemo(() => [...pastData, ...futureData], [pastData, futureData]);

  // Días de la curva que coinciden con un evento (para marcarlos en el chart).
  const eventMarkers = useMemo(() => {
    if (!events?.length || !mergedData.length) return [];
    const seen = new Set();
    const out = [];
    for (const ptn of mergedData) {
      const hit = events.find((ev) => eventCoversDate(ev, ptn.targetDate));
      if (hit && !seen.has(ptn.label)) {
        seen.add(ptn.label);
        out.push({ label: ptn.label, name: hit.name });
      }
    }
    return out;
  }, [events, mergedData]);

  const accData = useMemo(
    () =>
      (hist?.points || [])
        .filter((pt) => pt.actual != null)
        .map((pt) => ({
          targetDate: pt.targetDate,
          label: fechaLabel(pt, granularity),
          actual: pt.actual,
          yhat: pt.yhat,
        })),
    [hist, granularity],
  );

  const stats = useMemo(() => {
    const pts = futureData.filter((d) => d.yhat != null);
    if (!pts.length) return null;
    // "Proyectado" excluye lo ya realizado del período en curso (actualToDate).
    const total = pts.reduce((a, d) => a + Number(d.yhat || 0) - Number(d.actualToDate || 0), 0);
    let peak = pts[0];
    for (const d of pts) if (Number(d.yhat) > Number(peak.yhat)) peak = d;
    return { total, avg: total / pts.length, peak, n: pts.length };
  }, [futureData]);

  const wape = hist?.summary?.wape; // fracción ≥ 0 (puede superar 1), o null
  const hasBands = futureData.some((d) => d.banda95 || d.banda80);
  const hasData = futureData.length > 0;
  const upcomingEvents = useMemo(() => {
    const todayIso = new Date().toISOString().slice(0, 10);
    return (events || []).filter((e) => (e.endDate || e.startDate) >= todayIso).slice(0, 8);
  }, [events]);

  const granLabel = (GRANS.find((g) => g.key === granularity) || GRANS[0]).label;

  return (
    <div className="w-full min-w-0 forecasting p-4 md:p-6 space-y-5">
      {/* Header */}
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <h1 className="text-2xl font-semibold text-fg">{t('Forecasting & Programación')}</h1>
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-medium rounded-full bg-brand-50 text-brand-700">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
              Beta
            </span>
          </div>
          <p className="text-sm text-fg-muted">
            {t(HORIZON_TEXT[granularity] || HORIZON_TEXT.daily)}
            {' · '}
            {t('calculada con tu histórico real, feriados de Chile, pago de sueldos y crecimiento.')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setModalOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-bg hover:bg-bg-muted text-fg text-xs font-medium transition-colors"
          >
            <CalendarPlus className="w-3.5 h-3.5" />
            {t('Eventos')}
            {events.length > 0 && (
              <span className="px-1.5 py-0.5 rounded-full bg-brand-50 text-brand-700 text-[10px] font-bold">{events.length}</span>
            )}
          </button>
          <button
            onClick={reload}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white text-xs font-medium transition-colors disabled:opacity-60"
          >
            <RefreshCw className={cn("w-3.5 h-3.5", loading && "animate-spin")} />
            {t('Actualizar')}
          </button>
        </div>
      </header>

      {/* Controles: métrica + granularidad + horizonte + entidad (holding) */}
      <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3">
        <div className="inline-flex rounded-lg border border-border bg-bg p-0.5 text-xs flex-wrap">
          {METRICS.map((m) => {
            const Icon = m.icon;
            return (
              <button
                key={m.key}
                onClick={() => setMetric(m.key)}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-colors",
                  metric === m.key ? "bg-brand-500 text-white shadow-sm" : "text-fg-muted hover:text-fg hover:bg-bg-muted",
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {t(m.label)}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {series.length > 1 && (
            <select
              value={safeIdx}
              onChange={(e) => setEntityIdx(Number(e.target.value))}
              className="bg-bg border border-border rounded-lg px-2.5 py-1.5 text-xs text-fg focus:outline-none focus:border-brand-500"
              title={t('Entidad')}
            >
              {series.map((s, i) => (
                <option key={s.scopeId || i} value={i}>
                  {s.scopeId || t('Global')}
                </option>
              ))}
            </select>
          )}
          <div className="inline-flex rounded-lg border border-border bg-bg p-0.5 text-xs">
            {GRANS.map((g) => (
              <button
                key={g.key}
                onClick={() => setGranularity(g.key)}
                className={cn(
                  "px-3 py-1.5 rounded-md font-medium transition-colors",
                  granularity === g.key ? "bg-brand-500 text-white shadow-sm" : "text-fg-muted hover:text-fg hover:bg-bg-muted",
                )}
              >
                {t(g.label)}
              </button>
            ))}
          </div>
          {granularity === "daily" && (
            <div className="inline-flex rounded-lg border border-border bg-bg p-0.5 text-xs">
              {HORIZONS.map((h) => (
                <button
                  key={h}
                  onClick={() => setHorizon(h)}
                  className={cn(
                    "px-3 py-1.5 rounded-md font-medium transition-colors",
                    horizon === h ? "bg-brand-500 text-white shadow-sm" : "text-fg-muted hover:text-fg hover:bg-bg-muted",
                  )}
                >
                  {h}d
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="bg-bg border border-border rounded-xl shadow-card p-10 flex flex-col items-center justify-center gap-3 text-center">
          <RefreshCw className="w-7 h-7 text-brand-500 animate-spin" />
          <div className="text-sm font-medium text-fg">{t('Cargando proyección…')}</div>
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-status-error-soft border border-status-error/30 rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-status-error shrink-0" />
            <div className="text-sm text-fg">{error}</div>
          </div>
          <button
            onClick={reload}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-status-error text-white text-xs font-semibold hover:opacity-90 transition-opacity"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            {t('Reintentar')}
          </button>
        </div>
      )}

      {/* Sin proyección vigente */}
      {!loading && !error && !hasData && (
        <div className="bg-bg border border-dashed border-border rounded-xl p-10 flex flex-col items-center gap-2 text-center">
          <Brain className="w-9 h-9 text-fg-subtle" />
          <div className="text-base font-semibold text-fg">{t('Todavía no hay proyección para esta vista')}</div>
          <p className="text-sm text-fg-muted max-w-md">
            {granularity === "daily"
              ? t('La corrida nocturna aún no generó datos para esta métrica, o no hay histórico suficiente. Se actualiza cada madrugada.')
              : t('No hay períodos completos proyectados para esta métrica. Prueba la vista diaria o vuelve después de la próxima corrida nocturna.')}
          </p>
        </div>
      )}

      {/* Contenido principal */}
      {!loading && !error && hasData && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <KpiTile
              icon={metricMeta.icon}
              tone="brand"
              label={`${t('Total proyectado')} · ${granularity === "daily" ? `${horizon}d` : t(granLabel)}`}
              value={stats ? fmt(stats.total) : "—"}
              hint={stats ? `${stats.n} ${t('períodos')} · ${t(metricMeta.unit)}` : ""}
            />
            <KpiTile
              icon={BarChart3}
              tone="info"
              label={t('Promedio por período')}
              value={stats ? fmt(stats.avg) : "—"}
              hint={t(granLabel)}
            />
            <KpiTile
              icon={TrendingUp}
              tone="warning"
              label={t('Pico esperado')}
              value={stats ? fmt(stats.peak.yhat) : "—"}
              hint={stats ? stats.peak.label : ""}
            />
            <KpiTile
              icon={Target}
              tone="neutral"
              label={t('Error típico (WAPE)')}
              value={wape != null ? formatPercent(wape) : "—"}
              hint={wapeHint(hist?.summary, t)}
            />
          </div>

          {/* Chart de proyección */}
          <div className="bg-bg border border-border rounded-xl shadow-card p-5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
              <div>
                <h3 className="text-base font-semibold text-fg flex items-center gap-2">
                  <Brain className="w-4 h-4 text-brand-700" />
                  {t('Proyección')} · {t(metricMeta.label)}
                </h3>
                <p className="text-sm text-fg-muted">
                  {serie?.model
                    ? `${t('Modelo')}: ${serie.model}${serie.family ? ` · ${t(FAMILY_LABEL[serie.family] || serie.family)}` : ""}`
                    : t('Línea: proyección · banda: intervalo de predicción')}
                  {serie?.generatedAt && (
                    <>
                      {' · '}{t('generado')}{' '}
                      <RelativeTime value={serie.generatedAt} t={t} />
                    </>
                  )}
                </p>
              </div>
              <div className="flex items-center gap-3 text-xs flex-wrap">
                {pastData.length > 0 && (
                  <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: p.real }} />{t('Real')}</span>
                )}
                <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0.5" style={{ background: p.brand }} />{t('Proyección')}</span>
                {hasBands && (
                  <span className="inline-flex items-center gap-1.5"><span className="w-3 h-2 rounded-sm" style={{ background: p.band, opacity: 0.3 }} />{t('Rango probable')} {show95 ? "95%" : "80%"}</span>
                )}
                {eventMarkers.length > 0 && (
                  <span className="inline-flex items-center gap-1.5"><span className="w-3 h-0" style={{ borderTop: `2px dashed ${p.band}` }} />{t('Eventos')}</span>
                )}
                {hasBands && (
                  <button onClick={() => setShow95((v) => !v)} className="text-brand-700 hover:underline font-medium">
                    {show95 ? t('Ocultar 95%') : t('Mostrar 95%')}
                  </button>
                )}
              </div>
            </div>
            {!hasBands && (
              <div className="mb-3 flex items-start gap-2 text-[11px] text-fg-muted bg-bg-muted/40 border border-border rounded-lg p-2">
                <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {t('No se pudo estimar el rango de esta serie en esta corrida.')}
              </div>
            )}
            <div style={{ width: "100%", height: 320 }}>
              <ResponsiveContainer>
                <ComposedChart data={mergedData} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="fcBand95" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={p.band} stopOpacity={0.16} />
                      <stop offset="100%" stopColor={p.band} stopOpacity={0.02} />
                    </linearGradient>
                    <linearGradient id="fcBand80" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={p.band} stopOpacity={0.28} />
                      <stop offset="100%" stopColor={p.band} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={p.grid} vertical={false} />
                  <XAxis dataKey="label" stroke={p.tick} fontSize={11} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
                  <YAxis stroke={p.tick} fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(1)}k` : `${v}`)} />
                  <Tooltip content={<ForecastTooltip />} />
                  {todayLabel && (
                    <ReferenceLine x={todayLabel} stroke={p.ref} strokeDasharray="3 3" label={{ value: t('hoy'), position: "insideTopRight", fill: p.ref, fontSize: 10, fontWeight: 600 }} />
                  )}
                  {eventMarkers.map((m) => (
                    <ReferenceLine key={`ev-${m.label}`} x={m.label} stroke={p.band} strokeOpacity={0.55} strokeDasharray="2 3" />
                  ))}
                  {show95 && <Area dataKey="banda95" stroke="none" fill="url(#fcBand95)" isAnimationActive={false} connectNulls={false} />}
                  <Area dataKey="banda80" stroke="none" fill="url(#fcBand80)" isAnimationActive={false} connectNulls={false} />
                  <Line dataKey="yhat" stroke={p.brand} strokeWidth={2.5} dot={false} isAnimationActive={false} />
                  <Line dataKey="real" stroke={p.real} strokeWidth={2.5} dot={{ r: 2 }} connectNulls={false} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Precisión histórica + Eventos próximos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Precisión */}
            <div className="bg-bg border border-border rounded-xl shadow-card p-5">
              <h3 className="text-base font-semibold text-fg flex items-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-brand-700" /> {t('Precisión histórica')}
              </h3>
              <p className="text-sm text-fg-muted mb-4">
                {accuracySubtitle(hist?.summary, t)}
              </p>
              {accData.length === 0 ? (
                <div className="h-[220px] flex flex-col items-center justify-center gap-2 text-center text-sm text-fg-muted">
                  <Activity className="w-7 h-7 text-fg-subtle" />
                  {t('Aún no hay precisión registrada. Se llena automáticamente con las corridas nocturnas.')}
                </div>
              ) : (
                <div style={{ width: "100%", height: 220 }}>
                  <ResponsiveContainer>
                    <LineChart data={accData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={p.grid} vertical={false} />
                      <XAxis dataKey="label" stroke={p.tick} fontSize={10} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
                      <YAxis stroke={p.tick} fontSize={10} tickLine={false} axisLine={false} width={32} />
                      <Tooltip content={<AccuracyTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11, color: p.tick }} formatter={(v) => (v === "actual" ? t('Real') : t('Predicho'))} />
                      <Line dataKey="actual" stroke={p.real} strokeWidth={2.5} dot={false} isAnimationActive={false} />
                      <Line dataKey="yhat" stroke={p.band} strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Eventos próximos */}
            <div className="bg-bg border border-border rounded-xl shadow-card p-5">
              <div className="flex items-center justify-between mb-3 gap-2">
                <h3 className="text-base font-semibold text-fg flex items-center gap-2">
                  <CalendarPlus className="w-4 h-4 text-brand-700" /> {t('Próximos eventos')}
                </h3>
                <button onClick={() => setModalOpen(true)} className="text-xs text-brand-700 hover:underline inline-flex items-center gap-1">
                  <CalendarPlus className="w-3.5 h-3.5" /> {t('Gestionar')}
                </button>
              </div>
              {upcomingEvents.length === 0 ? (
                <p className="text-sm text-fg-muted py-3">
                  {t('No hay eventos próximos.')}{" "}
                  <button onClick={() => setModalOpen(true)} className="text-brand-700 hover:underline">{t('Agregar feriados o campañas')}</button>.
                </p>
              ) : (
                <ul className="space-y-2">
                  {upcomingEvents.map((e) => (
                    <li key={e.id} className="flex items-center gap-3 p-2.5 rounded-lg border border-border bg-bg">
                      <div className="text-center shrink-0 w-11">
                        <div className="text-base font-bold text-fg leading-none tabular-nums">
                          {new Date(`${e.startDate}T00:00:00`).getDate()}
                        </div>
                        <div className="text-[10px] uppercase text-fg-muted">
                          {new Date(`${e.startDate}T00:00:00`).toLocaleDateString("es-CL", { month: "short" })}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-fg truncate">{e.name}</div>
                        <div className="text-[11px] text-fg-muted">{t(KIND_LABEL[e.kind] || e.kind)}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* Cómo se calcula */}
          <div className="bg-bg-muted/40 border border-dashed border-border rounded-xl p-5">
            <div className="flex items-start gap-3">
              <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-700 flex items-center justify-center shrink-0">
                <Info className="w-4 h-4" />
              </span>
              <div className="text-xs text-fg-muted leading-relaxed">
                <div className="font-semibold text-fg mb-1">{t('Cómo se calcula')}</div>
                {t('Cada noche el servidor entrena modelos por entidad sobre tu histórico real: diario a 90 días y mensual a 12 meses (tendencia de crecimiento y estacionalidad anual), reconciliados para que semanas y meses sumen lo mismo. Ajusta por feriados oficiales de Chile, puentes y pago de sueldos (fin e inicio de mes); esos efectos de calendario se miden en tu histórico y solo se aplican si mejoraron la precisión en los últimos 90 días. Los eventos que cargues se aplican tal cual, con el impacto que les asignes. La banda muestra el intervalo de predicción (80% y 95%), calibrado con los errores del backtest. El error típico (WAPE) es un backtest de la corrida vigente: el modelo actual se compara con lo realmente ocurrido en las últimas ~8 semanas (hasta 13 meses en la vista mensual), fechas que no vio al entrenar. El período en curso suma lo ya realizado más lo proyectado para el resto.')}
              </div>
            </div>
          </div>
        </>
      )}

      {modalOpen && <EventsModal onClose={() => setModalOpen(false)} onChange={reloadEvents} />}
    </div>
  );
}
